/**
 * Question Bank API Client
 * Handles all API calls to the Question Bank backend
 */

import { projectId } from '../supabase/info';

import { bearerAuthHeader } from '../apiHeaders';
const BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-bank`;

// Types
export interface Question {
  id: string;
  text: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'boolean' | 'number' | 'date' | 'list_builder';
  category: string;
  subcategory?: string;
  required: boolean;
  critical: boolean;
  helpText?: string;
  placeholder?: string;
  description?: string;
  grouping: {
    isPartOfWizard: boolean;
    wizardId?: string;
    wizardName?: string;
    pageNumber?: number;
    questionIndex?: number;
    gridBehavior: 'inline' | 'wizard_only' | 'both';
  };
  requiresContext: boolean;
  relatedQuestionIds?: string[];
  conditionalOn?: {
    questionId: string;
    operator?: 'equals' | 'contains' | 'greaterThan';
    value: any;
  };
  options?: Array<{ value: string; label: string; description?: string }>;
  validation?: { min?: number; max?: number; pattern?: string; errorMessage?: string };
  examples?: string[];
  tags: string[];
  phase: 'discovery' | 'scoping' | 'implementation';
  source: 'pathkit_default' | 'consultant_custom' | 'imported';
  section?: string;
  order?: number;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  lastModified?: string;
  updatedBy?: string;
  updatedByName?: string;
  usageCount?: number;
}

export interface WizardTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  pages: Array<{
    id: string;
    title: string;
    description?: string;
    questionIds: string[];
  }>;
  allowSkip: boolean;
  showProgress: boolean;
  autoSave: boolean;
  placement: {
    autoLaunch: {
      enabled: boolean;
      trigger: 'on_create' | 'on_first_view' | 'never';
      categories: string[];
      conditions?: {
        configItemStatus?: string[];
        userRole?: string[];
      };
    };
    manualAccess: {
      showInCategoryActions: boolean;
      showInGridActions: boolean;
      showInCIDetail: boolean;
      showInWizardLibrary: boolean;
    };
    availableToRoles: string[];
    requiresConfigItem: boolean;
    allowMultipleRuns: boolean;
  };
  isDefault: boolean;
  createdAt: string;
  createdBy?: string;
  lastModified?: string;
}

export interface QuestionBank {
  consultingFirmId: string;
  version: string;
  questions: Record<string, Question>;
  wizardTemplates: Record<string, WizardTemplate>;
  categoryMappings: Record<string, {
    defaultQuestionIds: string[];
    recommendedQuestionIds: string[];
    wizardTemplateIds: string[];
  }>;
  createdAt: string;
  lastModified: string;
  customizationsCount: number;
}

// Helper function for API calls
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  
  console.log('[QuestionBank API] Request:', {
    method: options?.method || 'GET',
    url,
    timestamp: new Date().toISOString()
  });

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...bearerAuthHeader(),
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('[QuestionBank API] Error:', {
        status: response.status,
        statusText: response.statusText,
        error,
        url
      });
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    const data = await response.json();
    console.log('[QuestionBank API] Success:', {
      method: options?.method || 'GET',
      url,
      dataSize: JSON.stringify(data).length
    });
    
    return data;
  } catch (error) {
    console.error('[QuestionBank API] Network Error:', {
      url,
      error: error instanceof Error ? error.message : String(error),
      hint: 'Is the backend deployed? Run: ./deploy-backend-only.sh'
    });
    throw error;
  }
}

// ==================== QUESTION BANK ====================

/**
 * Populate question bank for a firm (initial setup)
 */
export async function populateQuestionBank(firmId: string): Promise<QuestionBank> {
  return apiFetch<QuestionBank>(`/populate/${firmId}`, {
    method: 'POST',
  });
}

/**
 * Get entire question bank for a firm
 */
export async function getQuestionBank(firmId: string): Promise<QuestionBank> {
  return apiFetch<QuestionBank>(`/${firmId}`);
}

// ==================== QUESTIONS ====================

/**
 * Get all questions for a firm
 */
export async function getAllQuestions(firmId: string): Promise<Question[]> {
  const data = await apiFetch<{ questions: Question[] }>(`/${firmId}/questions`);
  return data.questions;
}

/**
 * Get questions for a specific category
 */
export async function getQuestionsByCategory(
  firmId: string,
  category: string
): Promise<Question[]> {
  const data = await apiFetch<{ questions: Question[] }>(
    `/${firmId}/category/${category}/questions`
  );
  return data.questions;
}

/**
 * Get a single question by ID
 */
export async function getQuestion(firmId: string, questionId: string): Promise<Question> {
  const data = await apiFetch<{ question: Question }>(`/${firmId}/question/${questionId}`);
  return data.question;
}

/**
 * Create a new custom question
 */
export async function createQuestion(
  firmId: string,
  question: Partial<Question>
): Promise<Question> {
  const data = await apiFetch<{ question: Question }>(`/${firmId}/questions`, {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
  return data.question;
}

/**
 * Update an existing question
 */
export async function updateQuestion(
  firmId: string,
  questionId: string,
  updates: Partial<Question>
): Promise<Question> {
  const data = await apiFetch<{ question: Question }>(`/${firmId}/questions/${questionId}`, {
    method: 'PUT',
    body: JSON.stringify({ updates }),
  });
  return data.question;
}

/**
 * Delete a question
 */
export async function deleteQuestion(firmId: string, questionId: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/${firmId}/questions/${questionId}`, {
    method: 'DELETE',
  });
}

/**
 * Duplicate a question (useful for customizing default questions)
 */
export async function duplicateQuestion(
  firmId: string,
  questionId: string
): Promise<Question> {
  const data = await apiFetch<{ question: Question }>(`/${firmId}/questions/${questionId}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return data.question;
}

/**
 * Bulk delete questions
 */
export async function bulkDeleteQuestions(
  firmId: string,
  questionIds: string[]
): Promise<void> {
  await apiFetch<{ success: boolean }>(`/${firmId}/questions/bulk-delete`, {
    method: 'POST',
    body: JSON.stringify({ questionIds }),
  });
}

// ==================== WIZARDS ====================

/**
 * Get all wizards for a firm
 */
export async function getAllWizards(firmId: string): Promise<WizardTemplate[]> {
  const data = await apiFetch<{ wizards: WizardTemplate[] }>(`/${firmId}/wizards`);
  return data.wizards;
}

/**
 * Get wizards for a specific category
 */
export async function getWizardsByCategory(
  firmId: string,
  category: string
): Promise<WizardTemplate[]> {
  const data = await apiFetch<{ wizards: WizardTemplate[] }>(
    `/${firmId}/category/${category}/wizards`
  );
  return data.wizards;
}

/**
 * Get a single wizard by ID
 */
export async function getWizard(firmId: string, wizardId: string): Promise<WizardTemplate> {
  const data = await apiFetch<{ wizard: WizardTemplate }>(`/${firmId}/wizard/${wizardId}`);
  return data.wizard;
}

/**
 * Create a new custom wizard
 */
export async function createWizard(
  firmId: string,
  wizard: Partial<WizardTemplate>
): Promise<WizardTemplate> {
  const data = await apiFetch<{ wizard: WizardTemplate }>(`/${firmId}/wizards`, {
    method: 'POST',
    body: JSON.stringify({ wizard }),
  });
  return data.wizard;
}

/**
 * Update an existing wizard
 */
export async function updateWizard(
  firmId: string,
  wizardId: string,
  updates: Partial<WizardTemplate>
): Promise<WizardTemplate> {
  const data = await apiFetch<{ wizard: WizardTemplate }>(`/${firmId}/wizard/${wizardId}`, {
    method: 'PUT',
    body: JSON.stringify({ updates }),
  });
  return data.wizard;
}

/**
 * Delete a wizard
 */
export async function deleteWizard(firmId: string, wizardId: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/${firmId}/wizard/${wizardId}`, {
    method: 'DELETE',
  });
}

/**
 * Check which wizards should auto-launch for a config item
 */
export async function checkAutoLaunchWizards(
  firmId: string,
  category: string,
  configItemId: string
): Promise<WizardTemplate[]> {
  const data = await apiFetch<{ wizards: WizardTemplate[] }>(
    `/${firmId}/auto-launch-wizards`,
    {
      method: 'POST',
      body: JSON.stringify({ category, configItemId }),
    }
  );
  return data.wizards;
}

// ==================== CATEGORIES ====================

/**
 * Get all categories with their mappings
 */
export async function getCategories(firmId: string): Promise<Record<string, {
  defaultQuestionIds: string[];
  recommendedQuestionIds: string[];
  wizardTemplateIds: string[];
}>> {
  const bank = await getQuestionBank(firmId);
  return bank.categoryMappings;
}

/**
 * Update category mappings
 */
export async function updateCategoryMapping(
  firmId: string,
  category: string,
  mapping: {
    defaultQuestionIds?: string[];
    recommendedQuestionIds?: string[];
    wizardTemplateIds?: string[];
  }
): Promise<void> {
  await apiFetch<{ success: boolean }>(`/${firmId}/category/${category}/mapping`, {
    method: 'PUT',
    body: JSON.stringify({ mapping }),
  });
}

// ==================== SEARCH & FILTER ====================

/**
 * Search questions by text
 */
export function searchQuestions(questions: Question[], query: string): Question[] {
  const lowerQuery = query.toLowerCase();
  return questions.filter(q =>
    q.text.toLowerCase().includes(lowerQuery) ||
    q.id.toLowerCase().includes(lowerQuery) ||
    q.category.toLowerCase().includes(lowerQuery) ||
    q.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
    q.helpText?.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Filter questions by criteria
 */
export interface QuestionFilters {
  category?: string;
  phase?: 'discovery' | 'scoping' | 'implementation';
  source?: 'pathkit_default' | 'consultant_custom' | 'imported';
  isWizardQuestion?: boolean;
  required?: boolean;
  critical?: boolean;
  tags?: string[];
}

export function filterQuestions(
  questions: Question[],
  filters: QuestionFilters
): Question[] {
  return questions.filter(q => {
    if (filters.category && q.category !== filters.category) return false;
    if (filters.phase && q.phase !== filters.phase) return false;
    if (filters.source && q.source !== filters.source) return false;
    if (filters.isWizardQuestion !== undefined &&
        q.grouping.isPartOfWizard !== filters.isWizardQuestion) return false;
    if (filters.required !== undefined && q.required !== filters.required) return false;
    if (filters.critical !== undefined && q.critical !== filters.critical) return false;
    if (filters.tags && filters.tags.length > 0) {
      const hasAllTags = filters.tags.every(tag => q.tags.includes(tag));
      if (!hasAllTags) return false;
    }
    return true;
  });
}

/**
 * Sort questions
 */
export type QuestionSortField = 'text' | 'category' | 'createdAt' | 'usageCount' | 'order';
export type SortDirection = 'asc' | 'desc';

export function sortQuestions(
  questions: Question[],
  field: QuestionSortField,
  direction: SortDirection = 'asc'
): Question[] {
  const sorted = [...questions].sort((a, b) => {
    let aVal: any = a[field];
    let bVal: any = b[field];

    // Handle undefined values
    if (aVal === undefined) aVal = '';
    if (bVal === undefined) bVal = '';

    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Number comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  return sorted;
}